import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface PageMetaProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  publishedTime?: string;
  author?: string;
  tags?: string[];
  children?: React.ReactNode;
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  ogTitle,
  ogDescription,
  publishedTime,
  author,
  tags,
  children,
}: PageMetaProps) {
  const location = useLocation();
  const baseUrl = 'https://jiujiujiujiu.com';
  const fullUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  const mountedRef = useRef(false);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const removeMeta = (name: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      const el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) el.remove();
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = fullUrl;

    setMeta('og:type', ogType, true);
    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('og:site_name', '久久久久', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (publishedTime) setMeta('article:published_time', publishedTime, true);
    if (author) setMeta('article:author', author, true);

    if (tags) {
      tags.forEach((tag) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
      });
    }

    mountedRef.current = true;

    return () => {
      // Cleanup article:tag metas
      const articleTags = document.querySelectorAll('meta[property="article:tag"]');
      articleTags.forEach((el) => el.remove());
    };
  }, [title, description, keywords, fullUrl, ogType, ogImage, ogTitle, ogDescription, publishedTime, author, tags]);

  return <>{children}</>;
}